import { useEffect, useState } from "react";

/**
 * SplashScreen — animated intro overlay shown once per session when the
 * app is first opened. Delivers a "premium" first-impression:
 *   - Radar-style expanding PNJ badge with rotating orbit ring.
 *   - "SHUNTING ALERTS" wordmark that slides + fades in letter-by-letter.
 *   - Moving light streaks along a track line at the bottom.
 *   - Progress bar that fills in ~1.8s, then a smooth crossfade out.
 *
 * The parent unmounts this component after `onDone` fires, so no runtime
 * cost after the intro.
 */
const BRAND = "SHUNTING ALERTS";
const TAGLINE = "Startup & Departure Alert System";

export default function SplashScreen({ onDone, duration = 2200 }) {
  const [phase, setPhase] = useState("in"); // "in" → "out" → "done"

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("out"), duration);
    const t2 = setTimeout(() => {
      setPhase("done");
      onDone && onDone();
    }, duration + 550);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [duration, onDone]);

  if (phase === "done") return null;

  return (
    <div
      data-testid="splash-screen"
      className={`fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-[#050f18] transition-opacity duration-500 ${
        phase === "out" ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      {/* animated backdrop grid */}
      <div className="absolute inset-0 splash-grid opacity-40" />
      {/* radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.22),transparent_65%)]" />

      {/* horizontal moving light streaks (train-track vibes) */}
      <div className="absolute inset-x-0 top-1/2 mt-24 h-[2px] bg-[#0ea5e9]/15 overflow-hidden">
        <div className="splash-streak" />
        <div className="splash-streak splash-streak-delay" />
      </div>

      <div className="relative z-10 flex flex-col items-center gap-8">
        {/* Radar rings behind the logo */}
        <div className="relative">
          <span className="splash-ring absolute inset-0 rounded-full" />
          <span className="splash-ring splash-ring-2 absolute inset-0 rounded-full" />
          <span className="splash-ring splash-ring-3 absolute inset-0 rounded-full" />

          {/* PNJ badge — reused SVG structure from PnjLogo */}
          <svg
            width={128}
            height={128}
            viewBox="0 0 64 64"
            xmlns="http://www.w3.org/2000/svg"
            className="relative splash-badge"
          >
            <defs>
              <linearGradient id="splash-ring-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#0ea5e9" />
                <stop offset="55%" stopColor="#22d3ee" />
                <stop offset="100%" stopColor="#22c55e" />
              </linearGradient>
              <linearGradient id="splash-face-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0c1b2a" />
                <stop offset="100%" stopColor="#062036" />
              </linearGradient>
            </defs>
            <circle cx="32" cy="32" r="30" fill="url(#splash-face-grad)" />
            <circle
              cx="32"
              cy="32"
              r="26"
              fill="url(#splash-face-grad)"
              stroke="url(#splash-ring-grad)"
              strokeWidth="2.2"
              className="splash-badge-stroke"
            />
            {/* rotating sweep line */}
            <g className="splash-sweep" style={{ transformOrigin: "32px 32px" }}>
              <line
                x1="32"
                y1="32"
                x2="32"
                y2="8"
                stroke="#22d3ee"
                strokeWidth="1.5"
                strokeLinecap="round"
                opacity="0.85"
              />
            </g>
            <path
              d="M14 40 A 20 20 0 0 0 50 40"
              fill="none"
              stroke="#22c55e"
              strokeWidth="1.4"
              strokeLinecap="round"
              opacity="0.75"
            />
            <text
              x="32"
              y="38"
              textAnchor="middle"
              fontFamily="Manrope, 'IBM Plex Sans', sans-serif"
              fontWeight="800"
              fontSize="18"
              letterSpacing="1.2"
              fill="#e2f5ff"
            >
              PNJ
            </text>
          </svg>
        </div>

        {/* Wordmark: SHUNTING ALERTS — letters slide up with stagger */}
        <div className="flex flex-col items-center">
          <div className="flex gap-1 md:gap-1.5">
            {BRAND.split("").map((ch, i) => (
              <span
                key={`${ch}-${i}`}
                className="splash-letter font-display text-2xl md:text-4xl font-extrabold tracking-[0.25em] text-white"
                style={{ animationDelay: `${300 + i * 55}ms` }}
              >
                {ch === " " ? "\u00A0" : ch}
              </span>
            ))}
          </div>
          <div
            className="splash-tagline mt-3 text-[10px] md:text-xs uppercase tracking-[0.45em] text-[#7dd3fc]"
            style={{ animationDelay: "1300ms" }}
          >
            {TAGLINE}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-[3px] w-52 md:w-64 rounded-full bg-[#122032] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#0ea5e9] via-[#22d3ee] to-[#22c55e] splash-progress"
            style={{ animationDuration: `${duration - 200}ms` }}
          />
        </div>

        {/* Credit */}
        <div
          className="splash-tagline text-[10px] uppercase tracking-[0.35em] text-slate-500"
          style={{ animationDelay: "1600ms" }}
        >
          Created by <span className="text-[#7dd3fc]">C. Panji</span>
        </div>
      </div>

      {/* Inline scoped keyframes so this component is self-contained. */}
      <style>{`
        .splash-grid {
          background-image:
            linear-gradient(rgba(14,165,233,0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14,165,233,0.08) 1px, transparent 1px);
          background-size: 40px 40px;
          animation: splash-grid-pan 8s linear infinite;
        }
        @keyframes splash-grid-pan {
          0%   { background-position: 0 0, 0 0; }
          100% { background-position: 40px 40px, 40px 40px; }
        }

        .splash-badge {
          animation: splash-badge-in 900ms cubic-bezier(.2,.9,.25,1.15) both;
          filter: drop-shadow(0 0 24px rgba(14,165,233,0.55));
        }
        @keyframes splash-badge-in {
          0%   { opacity: 0; transform: scale(0.55) rotate(-8deg); }
          60%  { opacity: 1; transform: scale(1.05) rotate(2deg); }
          100% { opacity: 1; transform: scale(1) rotate(0); }
        }

        .splash-badge-stroke {
          stroke-dasharray: 170;
          stroke-dashoffset: 170;
          animation: splash-draw 900ms ease-out 100ms forwards;
        }
        @keyframes splash-draw {
          to { stroke-dashoffset: 0; }
        }

        .splash-sweep {
          animation: splash-sweep 1.6s linear infinite;
        }
        @keyframes splash-sweep {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        .splash-ring {
          border: 1px solid rgba(14,165,233,0.45);
          width: 128px; height: 128px;
          left: 50%; top: 50%;
          transform: translate(-50%, -50%) scale(1);
          animation: splash-ring 2.6s ease-out infinite;
        }
        .splash-ring-2 { animation-delay: 0.6s; }
        .splash-ring-3 { animation-delay: 1.2s; }
        @keyframes splash-ring {
          0%   { opacity: 0.9; transform: translate(-50%, -50%) scale(0.6); }
          80%  { opacity: 0.05; transform: translate(-50%, -50%) scale(2); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(2.2); }
        }

        .splash-letter {
          display: inline-block;
          opacity: 0;
          transform: translateY(18px);
          animation: splash-letter-in 520ms cubic-bezier(.2,.7,.25,1) forwards;
        }
        @keyframes splash-letter-in {
          to { opacity: 1; transform: translateY(0); }
        }

        .splash-tagline {
          opacity: 0;
          animation: splash-fade-in 700ms ease-out forwards;
        }
        @keyframes splash-fade-in {
          to { opacity: 1; }
        }

        .splash-progress {
          width: 0%;
          animation-name: splash-progress-fill;
          animation-timing-function: cubic-bezier(.3,.7,.4,1);
          animation-fill-mode: forwards;
        }
        @keyframes splash-progress-fill {
          from { width: 0%; }
          to   { width: 100%; }
        }

        .splash-streak {
          position: absolute;
          height: 2px;
          width: 30%;
          background: linear-gradient(90deg, transparent, #22d3ee, transparent);
          top: 0;
          left: -30%;
          animation: splash-streak 2.4s cubic-bezier(.4,.05,.2,1) infinite;
        }
        .splash-streak-delay { animation-delay: 1.2s; opacity: 0.55; }
        @keyframes splash-streak {
          0%   { left: -30%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
}
